import React, { useState } from 'react';
import intl from '$intl';
import ItemModel from '$fbusiness/models/item';

import { IonContentWrapper, IonFooterWrapper } from './styles';
import { Button, Counter } from '$gcomponents/primitives';
import { Flex } from '$gstyles/wrapper';
import CurrentStateModel, { initialCurrentState } from '$fbusiness/models/currentState';
import Img from '$gcomponents/primitives/img';
import { timestamp } from '$gbusiness/helpers/date';
import { currency, getInitial } from '$gbusiness/helpers/util';
import { Col, Div, Row } from '$gstyles';
import { oosEnabled } from '$fbusiness/helpers/util';
import { CartModifierModel } from '$fbusiness/models/cartItem';
import ModifierGroupModel from '$fbusiness/models/modifierGroup';
import ModifierModel from '$fbusiness/models/modifier';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { dialog } from '$gcomponents/reusables';

interface ItemDetailsViewProps {
  item: ItemModel;
  currentState?: CurrentStateModel;
  onAddCart: Function;
  onClose: Function;
}

const IMAGE_SIZE = '100%';
const THUMB_SIZE = 64;

const ItemDetailsView: React.FC<ItemDetailsViewProps> = ({
  item,
  currentState = initialCurrentState,
  onAddCart,
  onClose,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [memo, setMemo] = useState('');
  const [selectedModifiers, setSelectedModifiers] = useState<Array<CartModifierModel>>([]);
  const { ebrochure, hidePrice, isWholesale, settings: factorySettings } = currentState;
  const { files, name, sku, description, retailPrice, modifierGroups, wholesalePrice, settings } = item;
  const images = files || [];
  const hasMultipleImages = images.length > 1;
  const [imageIndex, setImageIndex] = useState(Math.max(images.findIndex((img) => img.isMain) || 0, 0));

  const price = isWholesale ? wholesalePrice : retailPrice;
  const tagText = intl('SCREEN.ITEMS.' + (isWholesale ? 'WHOLESALE' : 'RETAIL') + '_TAG');
  const currentImage = item?.itemImage;

  const orderOos = oosEnabled(item, factorySettings);
  const cantAdd = item.quantity != null && item.quantity <= 0 && !orderOos; // eslint-disable-line eqeqeq

  const qtyDecimal = item?.settings?.qtyDecimal || false;

  // Sort modifier groups by displayOrder then by id
  const sortedModifierGroups = [...(modifierGroups || [])].sort((a, b) => {
    const orderA = a.displayOrder ?? Infinity;
    const orderB = b.displayOrder ?? Infinity;
    if (orderA !== orderB) return orderA - orderB;
    return a.id - b.id;
  });

  // Helper function to sort modifiers within a group by id
  const sortModifiers = (modifiers: ModifierModel[]) => {
    return [...modifiers].sort((a, b) => a.id - b.id);
  };

  // Handler for modifier selection
  const handleModifierChange = (
    modifierGroup: ModifierGroupModel,
    modifier: ModifierModel,
    mgIndex: number,
    modifierIndex: number,
  ) => {
    const isMultiple = modifierGroup.multiple;
    const existing = selectedModifiers.find((m) => m.mgIndex === mgIndex && m.index === modifierIndex);

    if (!isMultiple) {
      // Radio button - single selection per group
      if (existing) {
        // Deselect
        setSelectedModifiers(selectedModifiers.filter((m) => m.mgIndex !== mgIndex));
      } else {
        // Select and remove any other selection in this group
        const newModifier: CartModifierModel = {
          mgIndex,
          index: modifierIndex,
          mgId: modifierGroup.id,
          modifierId: modifier.id,
          name: modifier.name,
          label: modifierGroup.name,
          qty: 1,
          price: modifier.price || 0,
        };
        setSelectedModifiers([...selectedModifiers.filter((m) => m.mgIndex !== mgIndex), newModifier]);
      }
    } else {
      // Checkbox - multiple selection with constraints
      const groupSelections = selectedModifiers.filter((m) => m.mgIndex === mgIndex);
      const totalQty = groupSelections.reduce((sum, m) => sum + m.qty, 0);

      if (existing) {
        // Cycle through quantities or deselect
        const maxChecks = modifier.maxQuantity || 1;
        const newQty = existing.qty >= maxChecks ? 0 : existing.qty + 1;

        if (newQty === 0) {
          // Deselect
          setSelectedModifiers(
            selectedModifiers.filter((m) => !(m.mgIndex === mgIndex && m.index === modifierIndex)),
          );
        } else {
          // Update quantity
          setSelectedModifiers(
            selectedModifiers.map((m) =>
              m.mgIndex === mgIndex && m.index === modifierIndex ? { ...m, qty: newQty } : m,
            ),
          );
        }
      } else {
        // Check if we can add more
        if (modifierGroup.maxQuantity && totalQty >= modifierGroup.maxQuantity) {
          dialog.alert({
            message: `Maximum ${modifierGroup.maxQuantity} selections allowed for ${modifierGroup.name}`,
          });
          return;
        }

        // Add new selection
        const newModifier: CartModifierModel = {
          mgIndex,
          index: modifierIndex,
          mgId: modifierGroup.id,
          modifierId: modifier.id,
          name: modifier.name,
          label: modifierGroup.name,
          qty: 1,
          price: modifier.price || 0,
        };
        setSelectedModifiers([...selectedModifiers, newModifier]);
      }
    }
  };

  // Validation: check if all required modifier groups are properly selected
  const validateModifiers = (): boolean => {
    if (!sortedModifierGroups || sortedModifierGroups.length === 0) {
      return false; // No modifiers, so valid
    }

    for (let mgIndex = 0; mgIndex < sortedModifierGroups.length; mgIndex++) {
      const group = sortedModifierGroups[mgIndex];
      const isRequired = !group.multiple || group.minQuantity > 0;

      if (!isRequired) {
        continue; // Skip optional groups
      }

      const groupSelections = selectedModifiers.filter((m) => m.mgIndex === mgIndex);
      const totalQty = groupSelections.reduce((sum, m) => sum + m.qty, 0);

      if (!group.multiple) {
        // Radio button - must have at least one selection
        if (totalQty === 0) {
          return true; // Invalid - required but nothing selected
        }
      } else {
        // Checkbox - must meet minQuantity requirement
        if (totalQty < group.minQuantity) {
          return true; // Invalid - doesn't meet minimum
        }
      }
    }

    return false; // All validations passed
  };

  const hasValidationError = validateModifiers();

  return (
    <>
      <IonContentWrapper imageSize={IMAGE_SIZE} thumbSize={THUMB_SIZE}>
        <Flex className="item-container">
          <div className="image-section">
            {hasMultipleImages && (
              <div className="left">
                {hasMultipleImages &&
                  (files || []).map((img, i) => {
                    return (
                      <Img
                        src={img?.sizes?.small || ''}
                        key={i}
                        cache={timestamp(img.createdAt)}
                        className={`thumb link ${imageIndex === i && 'selected'}`}
                        size="cover"
                        width={THUMB_SIZE + 'px'}
                        height={THUMB_SIZE + 'px'}
                        onClick={() => setImageIndex(i)}
                      />
                    );
                  })}
              </div>
            )}
            <Img
              src={currentImage || ''}
              className="flex main-image"
              size="contain"
              placeholder="No Image"
              initial={factorySettings?.imageInitial ? getInitial(name) : undefined}
              width={IMAGE_SIZE}
              height={IMAGE_SIZE}
            />
          </div>
          <div className="content-section">
            <div className="item-name">{name}</div>
            {!!(description || sku) && (
              <div className="description">
                {description && (
                  <Div flex={1} className="value">
                    {description}
                  </Div>
                )}
                {!!sku && (
                  <div className="sku">
                    <span>CODE:</span> {sku}
                  </div>
                )}
                {!!settings?.packing && (
                  <div className="packing">
                    <span>PACKING:</span> {settings?.packing}
                  </div>
                )}
                {!!settings?.skid && (
                  <div className="skid">
                    <span>SKID:</span> {settings?.skid}
                  </div>
                )}
                {!!settings?.lotNo && (
                  <div className="lotNo">
                    <span>LOT NO:</span> {settings?.lotNo}
                  </div>
                )}
                {!!settings?.binNo && (
                  <div className="binNo">
                    <span>BIN NO:</span> {settings?.binNo}
                  </div>
                )}
              </div>
            )}
            {!ebrochure && (
              <Row>
                <Col gridSize={6}>
                  <Flex className="quantity">
                    <div className="label">QTY: </div>
                    <Counter
                      value={quantity}
                      qtyDecimal={qtyDecimal || true}
                      className={cantAdd ? 'disabled' : ''}
                      size={18}
                      onChange={(qty) => setQuantity(qtyDecimal ? qty : Math.floor(qty))}
                    />
                    {cantAdd && <div className="error oos">{intl('COMMON.OUT_OF_STOCK')}</div>}
                  </Flex>

                  {!hidePrice && (
                    <div className="price-section">
                      <div className="price">{currency(price * quantity)}</div>
                      <div className="tag">{tagText}</div>
                    </div>
                  )}
                </Col>
                <Col gridSize={6} className="memo-section">
                  <textarea
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                    placeholder="Item memo (optional)"
                  />
                </Col>
              </Row>
            )}
          </div>
        </Flex>

        {/* Modifier Groups Section */}
        {sortedModifierGroups && sortedModifierGroups.length > 0 && (
          <div className="modifiers-section">
            <h3 className="modifiers-title">Customizations</h3>
            {sortedModifierGroups.map((group, mgIndex) => {
              const sortedMods = sortModifiers(group.modifiers || []);
              const groupSelections = selectedModifiers.filter((m) => m.mgIndex === mgIndex);
              const totalQty = groupSelections.reduce((sum, m) => sum + m.qty, 0);
              const isRequired = !group.multiple || group.minQuantity > 0;

              return (
                <div key={mgIndex} className="modifier-group">
                  <div className="modifier-group-header">
                    <span className="modifier-group-name">{group.name}</span>
                    {!!group.multiple && group.maxQuantity > 0 && (
                      <span className="modifier-group-info">
                        (Select {group.minQuantity > 0 ? `${group.minQuantity}-` : 'up to '}
                        {group.maxQuantity}){isRequired && <span className="required">*</span>}
                      </span>
                    )}
                    {!group.multiple && <span className="required">*</span>}
                  </div>

                  <div className="modifier-list">
                    {sortedMods.map((modifier, modifierIndex) => {
                      const selection = selectedModifiers.find(
                        (m) => m.mgIndex === mgIndex && m.index === modifierIndex,
                      );
                      const isSelected = selection && selection.qty > 0;
                      const numChecks = selection?.qty || 0;
                      const isMultiple = group.multiple;

                      const CheckIcon = isMultiple
                        ? isSelected
                          ? CheckBoxIcon
                          : CheckBoxOutlineBlankIcon
                        : isSelected
                        ? RadioButtonCheckedIcon
                        : RadioButtonUncheckedIcon;

                      return (
                        <div
                          key={modifierIndex}
                          className="modifier-item"
                          onClick={() => handleModifierChange(group, modifier, mgIndex, modifierIndex)}>
                          <div className="modifier-check">
                            <CheckIcon className="check-icon" />
                          </div>
                          <div className="modifier-name">{modifier.name}</div>
                          {!!isMultiple && numChecks > 1 && (
                            <span className="quantity-indicator">x{numChecks}</span>
                          )}
                          <div className="modifier-price">
                            {modifier.price && modifier.price > 0
                              ? currency(modifier.price * numChecks || modifier.price)
                              : ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </IonContentWrapper>
      <IonFooterWrapper justifyContent="space-around">
        {ebrochure ? (
          <Button className="third" onClick={() => onClose()}>
            {intl('BUTTON.CLOSE')}
          </Button>
        ) : (
          <>
            {/* <QuantityEdit
              label={intl('INPUT.LABEL.QUANTITY')}
              value={quantity}
              onChange={q => setQuantity(q)}
              name="quantity"
            /> */}
            <Button className="third" variant="outlined" onClick={() => onClose()}>
              {intl('BUTTON.CANCEL')}
            </Button>
            <Button
              className="half"
              disabled={cantAdd || hasValidationError}
              onClick={() => onAddCart(quantity, memo, selectedModifiers)}>
              {intl('BUTTON.ADD_CART')}
            </Button>
          </>
        )}
      </IonFooterWrapper>
    </>
  );
};

export default ItemDetailsView;
